import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import styled from 'styled-components';
import Panel from '../Panel';
import Break from '../Break';
import { BlueBorderButton, BlueButton } from 'components/Button';
import {
  LOCAL_RESOURCE_MAPPED_VALUES,
  NO_CONTROL_OPTION_VALUE,
  RESOURCE_TYPES,
  SIDEPANEL_STATES
} from 'utils/appConstants';
import { getEndDateTime } from 'utils/format';
import { get } from 'lodash';
import { createControl } from 'reducers/control/actions';
import {
  setSidePanel,
  revertSidePanel,
  closeSidePanel
} from 'reducers/sidePanel/actions';
import ControlType from 'components/ControlType';
import SelectableSearchInput from 'components/SelectableSearchInput';
import Text from 'components/Text';
import {
  SubPanel,
  SubPanelRow,
  PanelRowDescriptor,
  PanelRowTextForm,
  PanelButtonRow,
  LabledInput,
  InputUnitLabel,
  InputAndLabelContainer,
  MultipleInputRowContainer,
  DatePickerWrapper,
  DurationInput,
  PanelRowDisplay,
  FullPanel,
  StyledForm,
  PanelContent,
  RadioButtons,
  RadioOption,
  RadioInput
} from 'components/PanelComponents';
import moment from 'moment';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { determineBack } from 'utils/determineBack';
import { fetchAllTopoNodes } from 'api/toponode';
import { fetchAllIndeNodes } from 'api/indenode';
import { getAllCurves } from 'reducers/curve/actions';
import { DEVICE_CATEGORIES } from 'utils/appConstants';
import { TooltipRow } from 'components/Tooltip';

const DurationContainer = styled.div`
  display: flex;
  width: 100%;
  justify-content: space-between;
`;

const DurationInputLabel = styled.div`
  width: 28px;
  font-size: 12px;
  background-color: #014c6b;
  border-radius: 0 2px 2px 0;
  color: white;
  text-align: center;
  vertical-align: center;
  box-sizing: border-box;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const TimeUnitInput = styled.div`
  font-family: Crossten;
  display: flex;
  height: 24px;
  width: 47%;
  box-sizing: border-box;
`;

const DropdownContainer = styled.div`
  width: 100%;
`;

const StyledCalendarText = styled.div`
  width: 235px;
  font-style: italic;
  color: #333;
  align-self: center;
  padding: 7px;
`;

const StyledMessageText = styled.p`
  margin-top: 10px;
  font-style: italic;
`;

const StyledBulletedList = styled.ul`
  list-style: circle;
`;

const curveIdControlTypes = [
  'opModFreqWattCurveId',
  'opModHFRTMayTripCurveId',
  'opModHFRTMustTripCurveId',
  'opModHVRTMayTripCurveId',
  'opModHVRTMomentaryCessationCurveId',
  'opModHVRTMustTripCurveId',
  'opModLFRTMayTripCurveId',
  'opModLFRTMustTripCurveId',
  'opModLVRTMayTripCurveId',
  'opModLVRTMomentaryCessationCurveId',
  'opModLVRTMustTripCurveId',
  'opModVoltVarCurveId',
  'opModVoltWattCurveId',
  'opModWattPFCurveId',
  'opModWattVarCurveId'
];

const repeatingDatesTooltipMessage =
  'Controls will repeat on these dates at the same time as the initial control, and run for the same duration';

const NewControl = ({ isActive }) => {
  const dispatch = useDispatch();
  const [description, setDescription] = useState('');
  const [start, setStart] = useState(new Date());
  const [end, setEnd] = useState(moment(new Date()).add(900, 'seconds')._d);
  const [controlTypes, setControlTypes] = useState([]);
  const [durationHours, setDurationHours] = useState(0);
  const [durationMinutes, setDurationMinutes] = useState(15);
  const [durationSeconds, setDurationSeconds] = useState(0);
  const [repeatingControlDays, setRepeatingControlDays] = useState([]);
  const [randomizeDuration, setRandomizeDuration] = useState('');
  const [randomizeStart, setRandomizeStart] = useState('');
  const [responseReceivedReq, setResponseReceivedReq] = useState(1);
  const [responseStatusReq, setResponseStatusReq] = useState(1);
  const [responseUserReq, setResponseUserReq] = useState(1);
  const [nodeId, setNodeId] = useState('');
  const [parentNodeId, setParentNodeId] = useState(null);
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [waitingReturn, setWaitingReturn] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [parentNodeOptions, setParentNodeOptions] = useState([]);
  const [deviceCategory, setDeviceCategory] = useState([]);
  const sidePanel = useSelector(state => state.sidePanel);
  const data = useSelector(state => state.sidePanel.data);
  const curves = useSelector(state => state.curve.curves);

  useEffect(() => {
    moment.updateLocale('en', {
      longDateFormat: {
        LT: 'h:mm:ss A', // <----------- add :ss
        LLLL: 'dddd, MMMM Do YYYY LT'
      }
    });
    const getCurves = async () => {
      await dispatch(getAllCurves());
    };
    getCurves();
    if (data && data.parentTopoNodeId !== undefined) {
      setParentNodeId(data.parentTopoNodeId);
    } else if (data && data.parentIndeNodeId !== undefined) {
      setParentNodeId(data.parentIndeNodeId);
    } else {
      const fetchTopoNodesIndeNodes = async () => {
        const topoNodes = await fetchAllTopoNodes();
        const topoNodeIds = topoNodes
          .filter(
            topoNode =>
              topoNode.localNode !== LOCAL_RESOURCE_MAPPED_VALUES.REMOTE
          )
          .map(topoNode => topoNode.topoNodeId);
        const indeNodes = await fetchAllIndeNodes();
        const indeNodeIds = indeNodes
          .filter(
            indeNode =>
              indeNode.localNode !== LOCAL_RESOURCE_MAPPED_VALUES.REMOTE
          )
          .map(indeNode => indeNode.indeNodeId);
        setParentNodeOptions(topoNodeIds.concat(indeNodeIds));
      };
      fetchTopoNodesIndeNodes();
    }
    setIsLoading(false);
  }, []);

  useEffect(() => {
    if (isActive && waitingReturn !== null) {
      if (
        sidePanel.data &&
        sidePanel.data.curveId !== null &&
        sidePanel.data.curveId !== undefined
      ) {
        const { curveId } = sidePanel.data;
        const curve = curves.find(curve => curve.curveId === curveId);
        const updatedControlTypes = controlTypes.slice();
        updatedControlTypes[waitingReturn] = {
          ...updatedControlTypes[waitingReturn],
          curve
        };
        setControlTypes(updatedControlTypes);
        setWaitingReturn(null);
      }
    }
  }, [controlTypes, curves, isActive, sidePanel, waitingReturn]);

  useEffect(() => {
    const endDateTime = getEndDateTime(
      start,
      durationHours,
      durationMinutes,
      durationSeconds
    );
    setEnd(endDateTime);
    setStart(start);
    setRepeatingControlDays([]);
  }, [durationMinutes]);

  const handleInputChange = event => {
    const target = event.target;
    const value =
      target.value === NO_CONTROL_OPTION_VALUE ? null : target.value;
    const name = target.name;

    if (name === 'description') {
      setDescription(value);
    } else if (name === 'randomizeDuration') {
      setRandomizeDuration(value);
    } else if (name === 'randomizeStart') {
      setRandomizeStart(value);
    } else if (name === 'responseReceivedReq') {
      setResponseReceivedReq(value);
    } else if (name === 'responseStatusReq') {
      setResponseStatusReq(value);
    } else if (name === 'responseUserReq') {
      setResponseUserReq(value);
    }
  };

  const handleDurationChange = event => {
    const {
      target: { value, name }
    } = event;
    const updateState = { [name]: value };
    if (value === '') {
      updateState[name] = 0;
    } else if (name === 'durationHours') {
      setDurationHours(value);
    } else if (name === 'durationMinutes') {
      setDurationMinutes(value);
    } else if (name === 'durationSeconds') {
      setDurationSeconds(value);
    }
  };

  const handleStartChange = startDateTime => {
    if (!startDateTime) {
      return null;
    }
    const endDateTime = getEndDateTime(
      startDateTime,
      durationHours,
      durationMinutes,
      durationSeconds
    );
    setEnd(endDateTime);
    setStart(startDateTime);
    setRepeatingControlDays([]); // If the start date changes, remove all previously set repeating control days
    if (
      parseInt(durationHours) === 0 &&
      parseInt(durationMinutes) === 0 &&
      parseInt(durationSeconds) === 0
    ) {
      // Add additional 15 min durationMinutes state value to endtime
      setDurationMinutes(15);
    }
  };

  const handleEndChange = endDateTime => {
    if (!endDateTime) {
      return null;
    }
    setEnd(endDateTime);

    const startUnix = moment(start, 'LLLL').unix();

    const endUnix = moment(endDateTime, 'LLLL').unix();

    const endStartDifference = endUnix - startUnix;
    const hours = Math.floor(endStartDifference / 3600);
    const remainderAfterHours = endStartDifference % 3600;
    const minutes = Math.floor(remainderAfterHours / 60);
    const seconds = remainderAfterHours % 60;
    setDurationHours(hours);
    setDurationMinutes(minutes);
    setDurationSeconds(seconds);
  };

  /**
   * When a user selects a day on the calendar, handle adding or removing it.
   * If it has already been selected previously, it should be removed.
   * If it has not been selected before, it should be added.
   * Set these repeating control days in state.
   * @param {Date} selectedDay
   */
  const handleMultiDateSelect = selectedDay => {
    let newRepeatingControlDays = [];
    let dateIsAlreadyIncluded = false;

    // Go through all currently set days for repeating control
    repeatingControlDays.forEach(includedDay => {
      // Check if each of the previously set days is not the new day selected by the user
      if (includedDay.getTime() === selectedDay.getTime()) {
        dateIsAlreadyIncluded = true;
      }
      // If the day isn't the selected day, keep it in repeating control day list
      else {
        newRepeatingControlDays.push(includedDay);
      }
    });

    // If the day hasn't already been included, add it into the repeating control day list
    if (!dateIsAlreadyIncluded) {
      newRepeatingControlDays.push(selectedDay);
    }

    // Sort by earlier dates first
    newRepeatingControlDays.sort((a, b) => {
      return a.getTime() - b.getTime();
    });
    setRepeatingControlDays(newRepeatingControlDays);
  };

  const durationChangeBlur = event => {
    if (Date.parse(start)) {
      const endString = getEndDateTime(
        start,
        durationHours,
        durationMinutes,
        durationSeconds
      );
      setEnd(endString);
    }
  };

  const validate = () => {
    let passed = true;
    let errors = {};

    if (moment(start).isSame(end)) {
      passed = false;
      errors.durationHours = true;
      errors.durationMinutes = true;
      errors.durationSeconds = true;
    }
    // Verify description length
    if (description.length > 32) {
      passed = false;
      errors.description = true;
    }

    if (!/^\d+$/.test(durationHours)) {
      passed = false;
      errors.durationHours = true;
    }

    if (!/^\d+$/.test(durationMinutes)) {
      passed = false;
      errors.durationMinutes = true;
    }

    if (
      randomizeDuration &&
      (Math.sign(randomizeDuration) === 0 ||
        parseInt(randomizeDuration) < -3600 ||
        parseInt(randomizeDuration) > 3600)
    ) {
      passed = false;
      errors.randomizeDuration = true;
    }

    if (
      randomizeStart &&
      (Math.sign(randomizeStart) === 0 ||
        parseInt(randomizeStart) < -3600 ||
        parseInt(randomizeStart) > 3600)
    ) {
      passed = false;
      errors.randomizeStart = true;
    }

    if (isNaN(Math.sign(randomizeStart))) {
      passed = false;
      errors.randomizeStart = true;
    }

    if (!nodeId && !parentNodeId) {
      passed = false;
      errors.nodeId = true;
    }

    if (controlTypes.length === 0) {
      passed = false;
      errors.controlTypes = true;
    }

    setErrors(errors);
    return passed;
  };

  const handleSubmit = async e => {
    e.preventDefault();
    setIsSubmitting(true);

    if (!validate()) {
      setIsSubmitting(false);
      return;
    }
    const controlsObj = {};
    for (let i = 0; i < controlTypes.length; i++) {
      let controlType = controlTypes[i].controlType;

      if (controlType === NO_CONTROL_OPTION_VALUE) {
        errors[controlType] = true;
        setErrors({ ...errors });
        setIsSubmitting(false);
        return;
      }

      // handle true/false forms
      if (controlType === 'opModConnect' || controlType === 'opModEnergize') {
        controlsObj[controlType] = !!parseInt(controlTypes[i][controlType]);
      }

      if (
        controlType === 'opModFixedPFAbsorbW' ||
        controlType === 'opModFixedPFInjectW'
      ) {
        let excitation;
        const { displacement, multiplier } = controlTypes[i];
        if (controlType === 'opModFixedPFAbsorbW') {
          excitation = controlTypes[i].pfabsorbExcitation;
        } else {
          excitation = controlTypes[i].pfinjectExcitation;
        }
        controlsObj[controlType] = {
          displacement: parseInt(displacement),
          excitation: !!parseInt(excitation),
          multiplier: parseInt(multiplier)
        };
      }

      if (controlType === 'opModFixedVar') {
        const { refType, value } = controlTypes[i];
        controlsObj[controlType] = {
          refType: parseInt(refType),
          value: parseFloat(value) * 100
        };
      }

      if (controlType === 'opModFreqDroop') {
        const { dBOF, dBUF, kOF, kUF, openLoopTms } = controlTypes[i];
        controlsObj[controlType] = {
          dBOF: parseFloat(dBOF) * 1000,
          dBUF: parseFloat(dBUF) * 1000,
          kOF: parseFloat(kOF) * 1000,
          kUF: parseFloat(kUF) * 1000,
          openLoopTms: parseFloat(openLoopTms) * 100
        };
      }

      // handle curve ids
      if (curveIdControlTypes.indexOf(controlType) > -1) {
        if (controlTypes[i].curve) {
          controlsObj[controlType] = controlTypes[i].curve.curveId;
        } else {
          errors[controlType] = true;
          setErrors({ ...errors });
          setIsSubmitting(false);
          return;
        }
      }

      if (
        controlType === 'opModMaxLimW' ||
        controlType === 'opModFixedW' ||
        controlType === 'rampTms'
      ) {
        // opModMaxLimW (0 - 10000)
        // opModFixedW (-10000 - 10000)
        // rampTms (0 - 65535)
        controlsObj[controlType] =
          parseFloat(controlTypes[i][controlType]) * 100;
      }

      if (controlType === 'opModTargetVar' || controlType === 'opModTargetW') {
        const { value, multiplier } = controlTypes[i];
        controlsObj[controlType] = {
          value: parseInt(value),
          multiplier: parseInt(multiplier)
        };
      }
    }
    var startUnix = moment(start, 'LLLL').unix();

    const duration =
      parseInt(durationHours) * 3600 +
      parseInt(durationMinutes) * 60 +
      parseInt(durationSeconds);

    const requestObj = {
      description,
      start: startUnix,
      nodeId: nodeId || parentNodeId,
      duration,
      randomizeDuration: parseInt(randomizeDuration),
      randomizeStart: parseInt(randomizeStart),
      responseReceivedReq: !!parseInt(responseReceivedReq),
      responseStatusReq: !!parseInt(responseStatusReq),
      responseUserReq: !!parseInt(responseUserReq),
      ...controlsObj
    };

    if (deviceCategory.length > 0) {
      let hex = '';
      let dcIntValue = 0;
      for (let i = 0; i <= deviceCategory.length; i++) {
        const index = DEVICE_CATEGORIES.indexOf(deviceCategory[i]);
        if (index !== -1) {
          let conversion = Math.pow(2, index);
          dcIntValue += conversion;
        }
      }
      hex = dcIntValue.toString(16);

      requestObj.deviceCategory = hex;
    }

    // Create all controls including repeating days
    const controlStartTimes = [start, ...repeatingControlDays];
    let firstCreatedControlId;
    let controlCreatedIndex = 0;

    try {
      // Create all the controls as long as there hasn't been an error
      while (controlCreatedIndex < controlStartTimes.length) {
        const time = controlStartTimes[controlCreatedIndex];
        const createTime = moment(time, 'LLLL').unix();
        const newRequestObj = {
          ...requestObj,
          start: createTime
        };
        const response = await dispatch(createControl(newRequestObj));

        // Take the control ID we get back from the first response
        // and use that to open the sidepanel once this process is done
        if (!firstCreatedControlId && response.controlId) {
          firstCreatedControlId = response.controlId;
        }
        controlCreatedIndex++;
      }
      if (firstCreatedControlId) {
        dispatch(
          setSidePanel({
            entity: RESOURCE_TYPES.CONTROL,
            state: SIDEPANEL_STATES.READ,
            id: firstCreatedControlId
          })
        );
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleFormChange = obj => {
    const { index, key, value } = obj;
    // This slice is used often to avoid mutation of current state when setting state
    const updatedControlTypes = controlTypes.slice();
    updatedControlTypes[index][key] = value;
    setControlTypes(updatedControlTypes);
  };

  const addControlType = () => {
    const updatedControlTypes = controlTypes.slice();
    updatedControlTypes.push({
      controlType: NO_CONTROL_OPTION_VALUE,
      key: new Date().getTime()
    });
    setControlTypes(updatedControlTypes);
    setErrors({
      ...errors,
      controlTypes: false
    });
  };

  const removeControlType = index => {
    const curveName = controlTypes[index].controlType;
    const updatedControlTypes = controlTypes.slice();
    updatedControlTypes.splice(index, 1);
    const updatedErrors = { ...errors };
    delete updatedErrors[curveName];
    setControlTypes(updatedControlTypes);
    setErrors(updatedErrors);
  };

  const handleControlTypeSelect = (event, index) => {
    const curveName = controlTypes[index].controlType;
    const updatedControlTypes = controlTypes.slice();
    const { key } = updatedControlTypes[index];
    updatedControlTypes[index] = {
      controlType: event.target.value,
      key
    };
    const updatedErrors = { ...errors };
    delete updatedErrors[curveName];
    setControlTypes(updatedControlTypes);
    setErrors(updatedErrors);
  };

  const removeCurve = index => {
    const updatedControlTypes = controlTypes.slice();
    delete updatedControlTypes[index].curve;
    setControlTypes(updatedControlTypes);
  };

  // Function for node search
  const handleSearchInputChange = (selectedOptions, input) => {
    if (Array.isArray(selectedOptions)) {
      if (input === 'deviceCategory') {
        setDeviceCategory(selectedOptions.map(option => option.value));
      } else if (input === 'nodeId') {
        setNodeId(selectedOptions.map(option => option.value));
      }
      return;
    }
    if (input === 'deviceCategory') {
      setDeviceCategory(get(selectedOptions, 'value', ''));
    } else if (input === 'nodeId') {
      setNodeId(get(selectedOptions, 'value', ''));
    }
  };

  const setWaitingForReturn = index => {
    const curveName = controlTypes[index].controlType;
    const updatedErrors = { ...errors };
    delete updatedErrors[curveName];
    setWaitingReturn(index);
    setErrors(updatedErrors);
  };

  const determineBlockedEndTimes = () => {
    let minHour = 0;
    let minMinutes = 0;
    if (start || end) {
      if (start.getDate() === end.getDate()) {
        minHour = start.getHours();
        minMinutes = start.getMinutes();
      }
    }
    return {
      minHour,
      minMinutes
    };
  };

  /**
   * Creates a list of days past the starting date of the control
   * that a user may create repeating controls on
   * @returns {Array} - List of days that a user can select on calendar
   */
  const createIncludedDaysList = () => {
    const maxDaysToShow = 7;
    const possibleControlDays = [];
    for (let currentDay = 0; currentDay < maxDaysToShow; currentDay++) {
      const date = moment(start).add(currentDay + 1, 'd')._d;
      if (!repeatingControlDays.includes(date)) {
        possibleControlDays.push(date);
      }
    }
    return possibleControlDays;
  };

  if (!isActive || (isActive && isLoading)) {
    return null;
  } else {
    const takenControls = controlTypes.map(obj => obj.controlType);
    const { minHour, minMinutes } = determineBlockedEndTimes();
    return (
      <StyledForm onSubmit={handleSubmit}>
        <FullPanel>
          <Panel title="New Control" primaryHeader />
          <PanelContent>
            <SubPanel>
              <SubPanelRow>
                <PanelRowDescriptor>
                  <Text fontSize="12px">Description</Text>
                  <Text fontSize="12px">(Optional)</Text>
                </PanelRowDescriptor>
                <PanelRowTextForm
                  name="description"
                  onChange={handleInputChange}
                  isError={errors.description}
                  maxLength="32"
                  value={description}
                />
              </SubPanelRow>

              <SubPanelRow>
                <PanelRowDescriptor>Start Time</PanelRowDescriptor>
                <DatePickerWrapper>
                  <DatePicker
                    name="startDateTime"
                    placeholderText="Enter Start Date and Time"
                    popperPlacement="top-end"
                    selected={start}
                    onChange={handleStartChange}
                    showTimeSelect
                    timeFormat="h:mm:ss a"
                    timeIntervals={15}
                    dateFormat="MMMM d, yyyy h:mm:ss aa"
                    timeCaption="time"
                    autoComplete="off"
                    popperModifiers={{
                      offset: {
                        enabled: true,
                        offset: '30px, 0px'
                      }
                    }}
                    required
                  />
                </DatePickerWrapper>
              </SubPanelRow>

              <SubPanelRow>
                <PanelRowDescriptor>Duration</PanelRowDescriptor>
                {/* hours */}
                <MultipleInputRowContainer>
                  <InputAndLabelContainer width="30%">
                    <LabledInput
                      type="number"
                      name="durationHours"
                      value={durationHours}
                      onFocus={e => e.target.select()}
                      onChange={handleDurationChange}
                      onBlur={durationChangeBlur}
                      min="0"
                      max="1193045"
                      isError={errors.durationHours}
                    />
                    <InputUnitLabel>hrs</InputUnitLabel>
                  </InputAndLabelContainer>
                  {/* minutes */}
                  <InputAndLabelContainer width="30%">
                    <LabledInput
                      type="number"
                      name="durationMinutes"
                      value={durationMinutes}
                      onFocus={e => e.target.select()}
                      onChange={handleDurationChange}
                      onBlur={durationChangeBlur}
                      min="0"
                      max="60"
                      isError={errors.durationMinutes}
                    />
                    <InputUnitLabel>min</InputUnitLabel>
                  </InputAndLabelContainer>
                  {/* seconds */}
                  <InputAndLabelContainer width="30%">
                    <LabledInput
                      type="number"
                      name="durationSeconds"
                      value={durationSeconds}
                      onFocus={e => e.target.select()}
                      onChange={handleDurationChange}
                      onBlur={durationChangeBlur}
                      min="0"
                      max="60"
                      isError={errors.durationSeconds}
                    />
                    <InputUnitLabel>sec</InputUnitLabel>
                  </InputAndLabelContainer>
                </MultipleInputRowContainer>
              </SubPanelRow>

              <SubPanelRow>
                <PanelRowDescriptor>End Time</PanelRowDescriptor>
                <DatePickerWrapper>
                  <DatePicker
                    placeholderText="Enter End Date and Time"
                    popperPlacement="top-end"
                    selected={end}
                    onChange={handleEndChange}
                    showTimeSelect
                    timeFormat="h:mm:ss aa"
                    timeIntervals={15}
                    minDate={start}
                    minTime={
                      new Date(new Date().setHours(minHour, minMinutes, 0, 0))
                    }
                    maxTime={new Date(new Date().setHours(23, 59, 0, 0))}
                    dateFormat="MMMM d, yyyy h:mm:ss aa"
                    timeCaption="time"
                    autoComplete="off"
                    popperModifiers={{
                      offset: {
                        enabled: true,
                        offset: '30px, 0px'
                      }
                    }}
                    disabled={!Boolean(start)}
                  />
                </DatePickerWrapper>
              </SubPanelRow>
            </SubPanel>

            <SubPanel>
              <SubPanelRow>
                <PanelRowDescriptor alignSelf="start">
                  <TooltipRow
                    display={true}
                    showSmallIcon={true}
                    message={repeatingDatesTooltipMessage}
                  >
                    <Text fontSize="12px">Repeating Dates</Text>
                  </TooltipRow>
                  <Text fontSize="12px">(Optional)</Text>
                </PanelRowDescriptor>
                <div
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    fontSize: '12px'
                  }}
                >
                  <DatePickerWrapper>
                    <DatePicker
                      calendarClassName="multi-date-select"
                      name="multiDateSelect"
                      placeholderText="Select days for control to repeat"
                      popperPlacement="top-end"
                      selected={start}
                      onChange={handleMultiDateSelect}
                      dateFormat="MMMM d, yyyy"
                      autoComplete="off"
                      popperModifiers={{
                        offset: {
                          enabled: true,
                          offset: '30px, 0px'
                        }
                      }}
                      required
                      customInput={
                        <BlueBorderButton type="button">
                          {!!repeatingControlDays?.length
                            ? 'Add or remove dates'
                            : 'Add dates'}
                        </BlueBorderButton>
                      }
                      includeDates={createIncludedDaysList()}
                      highlightDates={repeatingControlDays}
                      shouldCloseOnSelect={false}
                    >
                      <StyledCalendarText>
                        Please only pick dates within the 7 days of your control
                        start date
                      </StyledCalendarText>
                    </DatePicker>
                  </DatePickerWrapper>
                  {!!repeatingControlDays?.length && (
                    <>
                      <StyledMessageText>
                        All controls will start at{' '}
                        {moment(start).format('h:mm:ss A')}
                      </StyledMessageText>
                      <StyledBulletedList>
                        {repeatingControlDays.map(day => (
                          <li key={day}>
                            {moment(day).format('MMMM DD, YYYY')}
                          </li>
                        ))}
                      </StyledBulletedList>
                    </>
                  )}
                </div>
              </SubPanelRow>
            </SubPanel>

            <Break margin="8px 0" />

            <SubPanel>
              <SubPanelRow>
                <PanelRowDescriptor>
                  <Text fontSize="12px">Device Category</Text>
                  <Text fontSize="12px">(Optional)</Text>
                </PanelRowDescriptor>
                <DropdownContainer>
                  <SelectableSearchInput
                    isMulti={true}
                    options={DEVICE_CATEGORIES}
                    onChange={selected =>
                      handleSearchInputChange(selected, 'deviceCategory')
                    }
                    isError={errors.deviceCategory}
                    name="deviceCategory-select"
                    selected={deviceCategory}
                  />
                </DropdownContainer>
              </SubPanelRow>

              <SubPanelRow>
                <PanelRowDescriptor>
                  <Text fontSize="12px">randomizeDuration</Text>
                  <Text fontSize="12px">(Optional)</Text>
                </PanelRowDescriptor>
                <DurationContainer>
                  <TimeUnitInput>
                    <DurationInput
                      type="number"
                      name="randomizeDuration"
                      onChange={handleInputChange}
                      min="-3600"
                      max="3600"
                      isError={errors.randomizeDuration}
                      value={randomizeDuration}
                    />
                    <DurationInputLabel>sec</DurationInputLabel>
                  </TimeUnitInput>
                </DurationContainer>
              </SubPanelRow>

              <SubPanelRow>
                <PanelRowDescriptor>
                  <Text fontSize="12px">randomizeStart</Text>
                  <Text fontSize="12px">(Optional)</Text>
                </PanelRowDescriptor>
                <DurationContainer>
                  <TimeUnitInput>
                    <DurationInput
                      type="number"
                      name="randomizeStart"
                      onChange={handleInputChange}
                      min="-3600"
                      max="3600"
                      isError={errors.randomizeStart}
                      value={randomizeStart}
                    />
                    <DurationInputLabel>sec</DurationInputLabel>
                  </TimeUnitInput>
                </DurationContainer>
              </SubPanelRow>

              <SubPanelRow>
                <PanelRowDescriptor>
                  <Text fontSize="12px">responseReceivedReq</Text>
                  <Text fontSize="12px">(Optional)</Text>
                </PanelRowDescriptor>
                <RadioButtons>
                  <RadioOption>
                    <label>
                      <RadioInput
                        name="responseReceivedReq"
                        onChange={handleInputChange}
                        type="radio"
                        value={1}
                        checked={parseInt(responseReceivedReq) === 1}
                      />
                      True
                    </label>
                  </RadioOption>
                  <RadioOption>
                    <label>
                      <RadioInput
                        name="responseReceivedReq"
                        onChange={handleInputChange}
                        type="radio"
                        value={0}
                        checked={parseInt(responseReceivedReq) === 0}
                      />
                      False
                    </label>
                  </RadioOption>
                </RadioButtons>
              </SubPanelRow>

              <SubPanelRow>
                <PanelRowDescriptor>
                  <Text fontSize="12px">responseStatusReq</Text>
                  <Text fontSize="12px">(Optional)</Text>
                </PanelRowDescriptor>
                <RadioButtons>
                  <RadioOption>
                    <label>
                      <RadioInput
                        name="responseStatusReq"
                        onChange={handleInputChange}
                        type="radio"
                        value={1}
                        checked={parseInt(responseStatusReq) === 1}
                      />
                      True
                    </label>
                  </RadioOption>
                  <RadioOption>
                    <label>
                      <RadioInput
                        name="responseStatusReq"
                        onChange={handleInputChange}
                        type="radio"
                        value={0}
                        checked={parseInt(responseStatusReq) === 0}
                      />
                      False
                    </label>
                  </RadioOption>
                </RadioButtons>
              </SubPanelRow>

              <SubPanelRow>
                <PanelRowDescriptor>
                  <Text fontSize="12px">responseUserReq</Text>
                  <Text fontSize="12px">(Optional)</Text>
                </PanelRowDescriptor>
                <RadioButtons>
                  <RadioOption>
                    <label>
                      <RadioInput
                        name="responseUserReq"
                        onChange={handleInputChange}
                        type="radio"
                        value={1}
                        checked={parseInt(responseUserReq) === 1}
                      />
                      True
                    </label>
                  </RadioOption>
                  <RadioOption>
                    <label>
                      <RadioInput
                        name="responseUserReq"
                        onChange={handleInputChange}
                        type="radio"
                        value={0}
                        checked={parseInt(responseUserReq) === 0}
                      />
                      False
                    </label>
                  </RadioOption>
                </RadioButtons>
              </SubPanelRow>
            </SubPanel>

            <Break margin="8px 0" />

            <SubPanel>
              <SubPanelRow>
                <PanelRowDescriptor>Assign to Node</PanelRowDescriptor>
                {parentNodeId !== null ? (
                  <PanelRowDisplay>{parentNodeId}</PanelRowDisplay>
                ) : (
                  <DropdownContainer>
                    <SelectableSearchInput
                      isMulti={false}
                      options={parentNodeOptions}
                      onChange={selected =>
                        handleSearchInputChange(selected, 'nodeId')
                      }
                      isError={errors.nodeId}
                      name="node-select"
                      selected={nodeId}
                    />
                  </DropdownContainer>
                )}
              </SubPanelRow>
            </SubPanel>

            <Break margin="8px 0" />

            <SubPanel>
              <SubPanelRow>
                <PanelRowDescriptor>Control Mode(s)</PanelRowDescriptor>
              </SubPanelRow>

              {controlTypes.map((obj, i) => {
                return (
                  <ControlType
                    key={obj.key}
                    controlObj={obj}
                    takenControls={takenControls}
                    handleFormChange={handleFormChange}
                    removeControlType={removeControlType}
                    removeCurve={removeCurve}
                    handleControlTypeSelect={handleControlTypeSelect}
                    index={i}
                    isError={errors[obj.controlType]}
                    setWaitingForReturn={setWaitingForReturn}
                  />
                );
              })}
            </SubPanel>

            {controlTypes.length < 23 ? (
              <SubPanel>
                <PanelButtonRow noBorder>
                  <BlueBorderButton
                    type="button"
                    onClick={addControlType}
                    isError={errors.controlTypes}
                  >
                    + Add A Control Mode
                  </BlueBorderButton>
                </PanelButtonRow>
              </SubPanel>
            ) : null}
          </PanelContent>
          <PanelButtonRow>
            <BlueBorderButton
              type="button"
              onClick={() =>
                determineBack(
                  sidePanel,
                  () => dispatch(revertSidePanel()),
                  () => dispatch(closeSidePanel())
                )
              }
            >
              Cancel
            </BlueBorderButton>
            <BlueButton type="submit" disabled={isSubmitting}>
              Save Control
            </BlueButton>
          </PanelButtonRow>
        </FullPanel>
      </StyledForm>
    );
  }
};

export default NewControl;
